import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuBuilding2 } from "react-icons/lu";

import SuppliersToolbar from "./supplierPageComponents/SuppliersToolbar";
import SuppliersActionBar from "./supplierPageComponents/SuppliersActionBar";
import SupplierFormModal from "./supplierPageComponents/SupplierFormModal";
import Avatar from "../../common/Avatar";
import ConfirmDialog from "../../common/ConfirmDialog";
import Toaster, { notify } from "../../common/Toast";
import TruncatedText from "../../common/TruncatedText";
import { formatPhone } from "../../utils/phone";
import {
  fetchSuppliers,
  addSupplier,
  editSupplier,
  deleteSuppliers,
} from "../../redux/action/supplierAction/SupplierAction";
import Loader from "../../common/Loader";
import Pagination from "../../common/Pagination";

export default function Suppliers() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    list: data,
    pagination,
    listLoading,
    addLoading,
    editLoading,
    deleteLoading,
  } = useSelector((state) => state.supplier);

  const [searchValue, setSearchValue] = useState("");
  const [selectedRow, setSelectedRow] = useState(new Map());
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // .................... Fetch (debounced search) ...........................
  const skipNextFetch = useRef(false);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    const handle = setTimeout(() => {
      dispatch(fetchSuppliers(searchValue, page, pageSize)).then((res) => {
        if (!res?.success) {
          notify.error("Failed to fetch suppliers", res?.error);
        } else if (!res.stale && res.pagination.page !== page) {
          skipNextFetch.current = true;
          setPage(res.pagination.page);
        }
      });
    }, 350);
    return () => clearTimeout(handle);
  }, [dispatch, searchValue, page, pageSize, reloadKey]);

  const refetch = () => setReloadKey((k) => k + 1);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(1);
    setSelectedRow(new Map());
  };

  const changePage = (p) => {
    setSelectedRow(new Map());
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changePageSize = (size) => {
    setSelectedRow(new Map());
    setPageSize(size);
    setPage(1);
  };

  // .................... Selection ...........................

  const selectedCount = selectedRow.size;
  const selectedNames = Array.from(selectedRow.keys());
  const singleSelected =
    selectedCount === 1
      ? { name: selectedNames[0], ...selectedRow.get(selectedNames[0]) }
      : null;

  const handleRowClick = (item) => {
    const updated = new Map(selectedRow);
    if (updated.has(item.name)) {
      updated.delete(item.name);
    } else {
      const { name, ...rest } = item;
      updated.set(name, rest);
    }
    setSelectedRow(updated);
  };

  const handleSelectAll = () => {
    if (selectedRow.size === 0) {
      setSelectedRow(new Map(data.map(({ name, ...rest }) => [name, rest])));
    } else {
      setSelectedRow(new Map());
    }
  };

  // .................... Add / Edit ...........................

  const openAddModal = () => {
    setFormMode("add");
    setFormOpen(true);
  };

  const openEditModal = () => {
    if (!singleSelected) return;
    setFormMode("edit");
    setFormOpen(true);
  };

  const handleFormSubmit = async (form) => {
    const action =
      formMode === "edit"
        ? editSupplier(singleSelected.name, form)
        : addSupplier(form);
    const res = await dispatch(action);
    if (res.success) {
      notify.success(
        formMode === "edit" ? "Successful Update" : "Successful Addition",
        formMode === "edit"
          ? "Supplier has been successfully updated."
          : "Supplier has been successfully added.",
      );
      setFormOpen(false);
      setSelectedRow(new Map());
      refetch();
    } else {
      notify.error(
        formMode === "edit"
          ? "Failed to update supplier"
          : "Failed to add supplier",
        res.error,
      );
    }
  };

  // .................... Delete ...........................

  const handleDelete = async () => {
    const res = await dispatch(deleteSuppliers(selectedNames));
    if (res.success) {
      notify.success(
        "Successful Deletion",
        "Supplier(s) have been successfully deleted.",
      );
      setSelectedRow(new Map());
      setDeleteConfirmOpen(false);
      refetch();
    } else {
      notify.error("Failed to delete supplier(s)", res.error);
    }
  };

  const canDelete = selectedCount > 0 && !!user?.admin;

  return (
    <>
      <div className={`w-full ${selectedCount > 0 ? "pb-24" : ""}`}>
        <div className="mx-auto w-full max-w-[1600px] px-3 py-4 sm:px-4 lg:px-6">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <SuppliersToolbar
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onAddClick={openAddModal}
            />

            {/* ---------------- Desktop table (md and up) ---------------- */}
            <div className="hidden md:block">
              <div className="max-h-[70vh] overflow-auto">
                <table className="w-full table-fixed text-left text-[13px]">
                  <colgroup>
                    <col className="w-10" />
                    <col className="w-[20%]" />
                    <col className="w-[20%]" />
                    <col className="hidden lg:table-column lg:w-[13%]" />
                    <col className="hidden lg:table-column lg:w-[15%]" />
                    <col className="hidden xl:table-column xl:w-[20%]" />
                    <col className="hidden lg:table-column lg:w-[13%]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-slate-50/95 text-slate-500 backdrop-blur">
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={
                            selectedCount > 0 && selectedCount === data.length
                          }
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                        />
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide">
                        Name
                      </th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide">
                        Email
                      </th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide lg:table-cell">
                        Contact
                      </th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide lg:table-cell">
                        Person
                      </th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide xl:table-cell">
                        Address
                      </th>
                      <th className="hidden px-4 py-3 text-[11px] font-semibold uppercase tracking-wide lg:table-cell">
                        Tax ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {listLoading && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-10 text-center text-slate-400"
                        >
                          <Loader fullScreen text=" Loading suppliers…" />
                        </td>
                      </tr>
                    )}
                    {!listLoading && data.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-10 text-center text-slate-400"
                        >
                          No suppliers found.
                        </td>
                      </tr>
                    )}
                    {!listLoading &&
                      data.map((item) => {
                        const isSelected = selectedRow.has(item.name);
                        return (
                          <tr
                            key={item.name}
                            onClick={() => handleRowClick(item)}
                            className={`cursor-pointer border-l-2 transition-colors ${
                              isSelected
                                ? "border-l-brand-500 bg-brand-50/60"
                                : "border-l-transparent hover:bg-slate-50"
                            }`}
                          >
                            <td
                              className="px-4 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleRowClick(item)}
                                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                              />
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800">
                              <div className="flex min-w-0 items-center gap-2.5">
                                <Avatar name={item.name} size={30} />
                                <TruncatedText text={item.name} />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-500">
                              <TruncatedText text={item.email} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">
                              <TruncatedText text={formatPhone(item.contactNumber)} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">
                              <TruncatedText text={item.person} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 xl:table-cell">
                              <TruncatedText text={item.address} />
                            </td>
                            <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">
                              <TruncatedText text={item.taxId} />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ---------------- Mobile cards (below md) ---------------- */}
            <div className="divide-y divide-slate-100 md:hidden">
              {listLoading && (
                <div  className="px-4 py-10 text-center text-[13px] text-slate-400">
                   <Loader fullScreen text="Loading Suppliers ..." />
                </div >
              )}
              {!listLoading && data.length === 0 && (
                <p className="px-4 py-10 text-center text-[13px] text-slate-400">
                  No suppliers found.
                </p>
              )}
              {!listLoading &&
                data.map((item) => {
                  const isSelected = selectedRow.has(item.name);
                  return (
                    <div
                      key={item.name}
                      onClick={() => handleRowClick(item)}
                      className={`flex items-start gap-3 border-l-2 px-4 py-3 transition-colors ${
                        isSelected
                          ? "border-l-brand-500 bg-brand-50/60"
                          : "border-l-transparent active:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRowClick(item)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
                      />
                      <Avatar name={item.name} size={36} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-[13px] font-medium text-slate-800">
                          <TruncatedText text={item.name} />
                        </div>
                        <TruncatedText
                          text={item.email}
                          className="text-[12px] text-slate-500"
                        />
                        {(item.person || item.contactNumber) && (
                          <TruncatedText
                            text={[item.person, formatPhone(item.contactNumber)]
                              .filter(Boolean)
                              .join(" · ")}
                            className="text-[12px] text-slate-400"
                          />
                        )}
                        {item.address && (
                          <TruncatedText
                            text={item.address}
                            className="text-[12px] text-slate-400"
                          />
                        )}
                        {item.taxId && (
                          <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                            <LuBuilding2 size={11} />
                            {item.taxId}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {!listLoading && (
            <Pagination
              page={pagination?.page || page}
              totalPages={pagination?.totalPages || 1}
              totalItems={pagination?.totalItems || 0}
              pageSize={pageSize}
              onPageChange={changePage}
              onPageSizeChange={changePageSize}
              pageSizeOptions={[10, 20, 50]}
              itemLabel={pagination?.totalItems === 1 ? "supplier" : "suppliers"}
              loading={listLoading}
            />
          )}
        </div>

        <SuppliersActionBar
          selectedCount={selectedCount}
          canEdit={selectedCount === 1}
          canDelete={canDelete}
          onEdit={openEditModal}
          onDelete={() => setDeleteConfirmOpen(true)}
          onClear={() => setSelectedRow(new Map())}
        />
      </div>

      <SupplierFormModal
        open={formOpen}
        mode={formMode}
        initialValue={formMode === "edit" ? singleSelected : null}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        loading={formMode === "edit" ? editLoading : addLoading}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={selectedCount === 1 ? "Delete supplier?" : "Delete suppliers?"}
        description={`This will permanently delete ${selectedCount} supplier${
          selectedCount > 1 ? "s" : ""
        }. All requests associated with ${
          selectedCount === 1 ? "it" : "them"
        } will also be deleted. This action cannot be undone.`}
        confirmLabel="Delete"
        tone="danger"
        loading={deleteLoading}
      />

      <Toaster />
    </>
  );
}